/**
 * Set code mappings for instant frontend parsing.
 * Contains ~50 most common set codes for quick lookup.
 *
 * Full set code validation happens on the backend using the complete
 * SET_CODE_MAP from tcg_pricing.py.
 */

export interface SetCodeEntry {
  code: string;
  scrydexId: string;
  displayName: string;
  /**
   * Extra single-token aliases (multi-word phrases won't match the search
   * parser's whitespace-tokenization). Mirrors the multi-key pattern in the
   * backend's SET_CODE_MAP.
   */
  aliases?: string[];
}

/**
 * Common set codes for instant frontend parsing.
 * Ordered roughly by popularity/recency.
 */
export const SET_CODES: SetCodeEntry[] = [
  // Mega Evolution era (2025-2026)
  { code: 'ME1', scrydexId: 'me1', displayName: 'Mega Evolution' },
  { code: 'ME2', scrydexId: 'me2', displayName: 'Phantasmal Flames' },
  { code: 'MEP', scrydexId: 'mep', displayName: 'Mega Evolution Promos' },

  // Scarlet & Violet era (2023-2025) - Most popular
  { code: 'PRE', scrydexId: 'sv8pt5', displayName: 'Prismatic Evolutions' },
  { code: 'SSK', scrydexId: 'sv8', displayName: 'Surging Sparks' },
  { code: 'STC', scrydexId: 'sv7', displayName: 'Stellar Crown' },
  { code: 'SFA', scrydexId: 'sv6pt5', displayName: 'Shrouded Fable' },
  { code: 'TWM', scrydexId: 'sv6', displayName: 'Twilight Masquerade' },
  { code: 'TEF', scrydexId: 'sv5', displayName: 'Temporal Forces' },
  { code: 'PAF', scrydexId: 'sv4pt5', displayName: 'Paldean Fates' },
  { code: 'PAR', scrydexId: 'sv4', displayName: 'Paradox Rift' },
  { code: '151', scrydexId: 'sv3pt5', displayName: 'Pokemon 151' },
  { code: 'OBF', scrydexId: 'sv3', displayName: 'Obsidian Flames' },
  { code: 'PAL', scrydexId: 'sv2', displayName: 'Paldea Evolved' },
  { code: 'SVI', scrydexId: 'sv1', displayName: 'Scarlet & Violet' },
  { code: 'SVP', scrydexId: 'svp', displayName: 'SV Black Star Promos' },
  { code: 'JTG', scrydexId: 'sv9', displayName: 'Journey Together' },
  { code: 'DRV', scrydexId: 'sv10', displayName: 'Destined Rivals' },

  // Sword & Shield era (2020-2023)
  { code: 'CRZ', scrydexId: 'swsh12pt5', displayName: 'Crown Zenith' },
  { code: 'SIT', scrydexId: 'swsh12', displayName: 'Silver Tempest' },
  { code: 'LOR', scrydexId: 'swsh11', displayName: 'Lost Origin' },
  { code: 'ASR', scrydexId: 'swsh10', displayName: 'Astral Radiance' },
  { code: 'BRS', scrydexId: 'swsh9', displayName: 'Brilliant Stars' },
  { code: 'FST', scrydexId: 'swsh8', displayName: 'Fusion Strike' },
  { code: 'EVS', scrydexId: 'swsh7', displayName: 'Evolving Skies' },
  { code: 'CRE', scrydexId: 'swsh6', displayName: 'Chilling Reign' },
  { code: 'BST', scrydexId: 'swsh5', displayName: 'Battle Styles' },
  { code: 'VIV', scrydexId: 'swsh4', displayName: 'Vivid Voltage' },
  { code: 'CEL', scrydexId: 'cel25', displayName: 'Celebrations' },
  {
    code: 'CCC',
    scrydexId: 'cel25c',
    displayName: 'Celebrations Classic Collection',
    aliases: ['CC'],
  },
  { code: 'SHF', scrydexId: 'swsh45', displayName: 'Shining Fates' },
  { code: 'CPA', scrydexId: 'swsh35', displayName: "Champion's Path" },
  { code: 'PGO', scrydexId: 'pgo', displayName: 'Pokemon GO' },

  // Sun & Moon era
  { code: 'HIF', scrydexId: 'sm115', displayName: 'Hidden Fates' },
  { code: 'UNM', scrydexId: 'sm11', displayName: 'Unified Minds' },
  { code: 'UNB', scrydexId: 'sm10', displayName: 'Unbroken Bonds' },
  { code: 'TEU', scrydexId: 'sm9', displayName: 'Team Up' },

  // XY era
  { code: 'EVO', scrydexId: 'xy12', displayName: 'Evolutions' },
  { code: 'GEN', scrydexId: 'g1', displayName: 'Generations' },

  // Vintage/WOTC era
  { code: 'BS', scrydexId: 'base1', displayName: 'Base Set' },
  { code: 'JU', scrydexId: 'base2', displayName: 'Jungle' },
  { code: 'FO', scrydexId: 'base3', displayName: 'Fossil' },
  { code: 'TR', scrydexId: 'base5', displayName: 'Team Rocket' },
  { code: 'GH', scrydexId: 'gym1', displayName: 'Gym Heroes' },
  { code: 'GC', scrydexId: 'gym2', displayName: 'Gym Challenge' },
  { code: 'N1', scrydexId: 'neo1', displayName: 'Neo Genesis' },
  { code: 'N2', scrydexId: 'neo2', displayName: 'Neo Discovery' },
  { code: 'N3', scrydexId: 'neo3', displayName: 'Neo Revelation' },
  { code: 'N4', scrydexId: 'neo4', displayName: 'Neo Destiny' },
  { code: 'LC', scrydexId: 'base6', displayName: 'Legendary Collection' },
];

/**
 * Build a Map of set codes to SetCodeEntry for O(1) lookup.
 * Keys are uppercase for case-insensitive matching.
 */
export function buildSetCodeMap(): Map<string, SetCodeEntry> {
  const map = new Map<string, SetCodeEntry>();

  for (const entry of SET_CODES) {
    // Add the code itself (uppercase)
    map.set(entry.code.toUpperCase(), entry);

    // Also add the Scrydex ID (lowercase becomes uppercase for lookup)
    // This allows users to type "sv3" or "SV3" and match Obsidian Flames
    map.set(entry.scrydexId.toUpperCase(), entry);

    // Register any extra aliases (e.g. 'CC' for Celebrations Classic Collection)
    for (const alias of entry.aliases ?? []) {
      map.set(alias.toUpperCase(), entry);
    }
  }

  return map;
}
